import type { ContextHubItem } from '@/_shared/lib/context-hub';

const CONTEXT_RETRIEVAL_TOOLS = new Set([
  'mcp__context__list_context_items',
  'mcp__context__search_context',
  'mcp__context__get_context_item',
]);

type ContextKind = ContextHubItem['kind'];

export interface ContextResultItem {
  id: string;
  kind: ContextKind;
  title: string;
  isSkill: boolean;
}

export type ParsedContextToolResult =
  | { state: 'ready'; label: 'Found context' | 'Consulted context'; items: ContextResultItem[] }
  | { state: 'empty'; label: 'Found context' | 'Consulted context' }
  | { state: 'unavailable'; label: 'Found context' | 'Consulted context'; message?: string };

export function isContextRetrievalTool(toolName: string): boolean {
  return CONTEXT_RETRIEVAL_TOOLS.has(toolName);
}

function isContextKind(value: unknown): value is ContextKind {
  return value === 'generated' || value === 'note' || value === 'memory' || value === 'skill';
}

function parseItem(value: unknown): ContextResultItem | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const item = value as Record<string, unknown>;
  if (typeof item['id'] !== 'string' || typeof item['title'] !== 'string') return null;

  // Pre-migration transcripts stored promoted skills as notes with adherence=skill.
  const kind = item['kind'] === 'note' && item['adherence'] === 'skill' ? 'skill' : item['kind'];
  if (!isContextKind(kind)) return null;

  return {
    id: item['id'],
    kind,
    title: item['title'],
    isSkill: kind === 'skill',
  };
}

export function parseContextToolResult(toolName: string, content: string): ParsedContextToolResult | null {
  if (!isContextRetrievalTool(toolName)) return null;
  const label = toolName === 'mcp__context__get_context_item' ? 'Consulted context' : 'Found context';

  let parsed: unknown;
  try {
    parsed = JSON.parse(content) as unknown;
  } catch {
    // MCP failures are plain prose (not-found / blocked). Malformed or truncated JSON
    // usually starts with `{`/`[` — keep those as a generic unavailable state.
    const message = content.trim();
    if (message && !message.startsWith('{') && !message.startsWith('[')) {
      return { state: 'unavailable', label, message };
    }
    return { state: 'unavailable', label };
  }

  const rawItems = Array.isArray(parsed) ? parsed : [parsed];
  if (rawItems.length === 0) return { state: 'empty', label };

  const items = rawItems.map(parseItem).filter((item): item is ContextResultItem => item !== null);
  if (items.length === 0) return { state: 'unavailable', label };
  return { state: 'ready', label, items };
}
